import React from 'react';
import { Grid } from "@progress/kendo-react-grid";
import { Component } from "puzzle";
import { checkVerticalScroll, removeUnnecessaryAttributes, setTableCaption } from "../Util";
import { GridProps } from "@progress/kendo-react-grid/dist/npm/interfaces/GridProps";
import _ from "lodash";
import classNames from "classnames";
import DragAndDropWrapper from "../../UI/DragAndDrop/DragAndDropWrapper";

export interface PROPS extends GridProps {
  captionText?: string,
  children: any,
  data: any[],
  dNdMessage?: string,
  dragAndDropEnabled?: boolean,
  uploadFiles?: Function,
  wrapperProps?: {
    className?: string
  }
}

class KendoGridAbstract extends Component<PROPS, {}> {
  gridRef: any = null;

  static defaultProps: PROPS = {
    children: null,
    data: [],
    dragAndDropEnabled: false
  };

  /**
   * @inheritDoc
   */
  moduleDidMount() {
    const { captionText } = this.props;
    this.updateGridHeader();
    captionText && setTableCaption(this.gridRef.element, captionText);
    removeUnnecessaryAttributes(this.gridRef.element);
  }

  /**
   * @inheritDoc
   */
  componentDidUpdate = (prevProps: PROPS) => {
    const { captionText } = this.props;
    this.updateGridHeader();
    captionText && setTableCaption(this.gridRef.element, captionText);
    removeUnnecessaryAttributes(this.gridRef.element);
  };

  /**
   * Drag leave event handler
   * @param event
   */
  dragLeaveHandler = (event: React.DragEvent) => {
    if (this.state.dragging) {
      this.setState({
        dragging: false
      });
    }
  };

  /**
   * Drag leave event handler
   * @param event
   */
  dragOverHandler = (event: React.DragEvent): void => {
    if (this.props.dragAndDropEnabled) {
      const { dragging } = this.state;

      if (!dragging) {
        this.setState({
          dragging: true
        });
      }
    }
  };

  /**
   * Prevents default behavior so files are present on drop
   * @param event
   */
  maskedDragOverHandler = (event: React.DragEvent) => {
    event.preventDefault();
  };

  /**
   * Drop event handler handler
   * @param e
   */
  onDropHandler = (e: React.DragEvent) => {
    e.preventDefault();
    const {
      files,
      items
    } = e.dataTransfer;

    const directoryExists = [...items].some((fileObject: DataTransferItem | null) => fileObject?.webkitGetAsEntry()?.isDirectory);
    this.setState({ dragging: false }, () => {
      let { uploadFiles } = this.props;
      directoryExists ? this.dispatch('invalidDropWarning') : (uploadFiles && uploadFiles(files))
    });
  };

  /**
   * Grid renderer
   */
  gridRenderer = () => {
    const { children, data, className } = this.props;
    const kendoGridProps: any = _.omit(this.props, ['children', 'captionText', 'dragAndDropEnabled', 'dNdMessage', 'uploadFiles']);
    const gridClassName = classNames(`pd-abstract-grid ${className}`, {
      'tp-grid-empty': !data?.length && data.length !== undefined
    })

    return (
      <Grid
        ref={(ref: any) => this.gridRef = ref}
        {...kendoGridProps}
        className={gridClassName}
      >
        {children}
      </Grid>
    )
  }

  /**
   * Method for scrollbar update
   */
  updateGridHeader = () => {
    const grid = this.gridRef?.element;
    if (grid) {
      const gridBody = grid.querySelector('table.k-grid-table');
      const gridHeader = grid.querySelector('div.k-grid-header');
      checkVerticalScroll(gridBody, gridHeader, grid);
    }
  }

  render() {
    const {
      dNdMessage,
      wrapperProps = undefined
    } = this.props;
    const { dragging } = this.state;

    return (
      <>
        <div
          onDragOver={this.dragOverHandler}
          {...wrapperProps}
        >
          <DragAndDropWrapper
            show={dragging}
            dragLeaveHandler={this.dragLeaveHandler}
            onDropHandler={this.onDropHandler}
            dragOverHandler={this.maskedDragOverHandler}
            dNdMessage={dNdMessage}
          />
          {this.gridRenderer()}
        </div>
      </>
    );
  }
}

export default KendoGridAbstract;
